import { Container, Alert, Button, Spinner } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import ProfileEditContain from "../../Components/Jobseeker/Profile";
import { Fragment, useMemo, useRef, useState, useEffect } from "react";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { useParams } from "react-router";
import { showToast } from "../../Components/Common/Toast";
import { updateResumeAvatar } from "../../redux/jobseeker/update_resume/actions";

const PROFILE_SECTION_ALERT_KEYS = [
  { key: "education", label: "Education Background" },
  { key: "work_experience", label: "Work Experience" },
  { key: "work_sample", label: "Work Sample" },
];

/** DOM ids on section cards in Jobseeker profile (scroll targets). */
const PROFILE_SECTION_ANCHOR_IDS = {
  education: "profile-section-education",
  work_experience: "profile-section-work-experience",
  work_sample: "profile-section-work-sample",
};

const scrollToProfileSection = (sectionKey) => {
  const id = PROFILE_SECTION_ANCHOR_IDS[sectionKey];
  if (!id) return;
  document.getElementById(id)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
};

const UpdateProfilePage = () => {
  const dispatch = useDispatch();
  const { cand_id } = useParams();
  const resumeFileInputRef = useRef(null);
  const [isResumeUploading, setIsResumeUploading] = useState(false);
  const updateResumeReducer = useSelector((state) => state.updateReducer);
  const isResumeBusy =
    isResumeUploading || Boolean(updateResumeReducer?.isLoading);

  const jobseekerData = useSelector(
    (state) => state.fetchJobseekerDataReducer.data
  );
  const profileData = jobseekerData?.data;
  const hasJobseekerData = Boolean(profileData);

  const resumeNotUploaded =
    profileData?.isResumeUploaded === false ||
    profileData?.is_uploaded_resume === false;

  const pendingIncompleteSections = useMemo(() => {
    if (!profileData) {
      return [];
    }
    const sections = profileData.profile_completion_sections;
    const incomplete = [];

    if (sections) {
      PROFILE_SECTION_ALERT_KEYS.forEach(({ key, label }) => {
        const meta = sections[key];
        if (meta && !meta.complete) {
          incomplete.push({ key, label });
        }
      });
      return incomplete;
    }

    const education = profileData.education;
    if (!Array.isArray(education) || education.length === 0) {
      incomplete.push({ key: "education", label: "Education Background" });
    }
    const workExperience = profileData.work_experience;
    if (!Array.isArray(workExperience) || workExperience.length === 0) {
      incomplete.push({ key: "work_experience", label: "Work Experience" });
    }
    const workSample = profileData.work_sample;
    if (!Array.isArray(workSample) || workSample.length === 0) {
      incomplete.push({ key: "work_sample", label: "Work Sample" });
    }
    return incomplete;
  }, [profileData]);

  const showResumeAlert = hasJobseekerData && resumeNotUploaded;
  const showProfileSectionsAlert =
    hasJobseekerData && !resumeNotUploaded && pendingIncompleteSections.length > 0;

  const handleResumeFileChange = (event) => {
    const file = event.target.files?.[0];
    const input = event.target;
    if (input) input.value = "";
    if (!file) return;
    const allowedExts = ["pdf", "doc", "docx", "txt"];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!allowedExts.includes(ext)) {
      showToast("error", "Invalid file format! Only PDF, DOC, DOCX, and TXT files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast("error", "Error: File size exceeds 5MB limit!");
      return;
    }
    setIsResumeUploading(true);
    const formData = new FormData();
    formData.append("jobseeker_resume_file", file);
    dispatch(updateResumeAvatar(formData));
  };

  // Clear local uploading flag when saga finishes (toast/refetch runs in EditmyProfile).
  useEffect(() => {
    if (!updateResumeReducer?.isLoading && isResumeUploading) {
      setIsResumeUploading(false);
    }
  }, [updateResumeReducer?.isLoading, isResumeUploading]);

  return (
    <Fragment>
      <Breadcrumbs
        title={cand_id ? "Candidates Details" : "Update Profile"}
        mainTitle={cand_id ? "Candidates Details" : "Update Profile"}
      />
      {showResumeAlert && (
        <Alert
          color="warning"
          className="mb-3 d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-3"
        >
          <div className="flex-grow-1">
            <strong>Upload your resume!</strong> Our AI will parse your resume
            and automatically update your profile details for you.
          </div>
          <div className="flex-shrink-0">
            <input
              ref={resumeFileInputRef}
              type="file"
              id="resume-upload-banner-input"
              className="d-none"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleResumeFileChange}
            />
            <Button
              color="primary"
              className="text-nowrap"
              disabled={isResumeBusy}
              onClick={() => resumeFileInputRef.current?.click()}
              type="button"
            >
              {isResumeBusy ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                "Upload resume"
              )}
            </Button>
          </div>
        </Alert>
      )}
      {showProfileSectionsAlert && (
        <Alert color="warning" className="mb-3">
          Please update your{" "}
          {pendingIncompleteSections.map((item, idx) => {
            const anchorId = PROFILE_SECTION_ANCHOR_IDS[item.key];
            return (
              <Fragment key={item.key}>
                {idx > 0 ? " / " : null}
                <a
                  href={anchorId ? `#${anchorId}` : undefined}
                  className="update-profile-link fw-bold text-decoration-underline"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToProfileSection(item.key);
                  }}
                >
                  {item.label}
                </a>
              </Fragment>
            );
          })}{" "}
          to complete your profile.
        </Alert>
      )}
      <Container fluid={true}>
        <ProfileEditContain />
      </Container>
    </Fragment>
  );
};

export default UpdateProfilePage;
